import React, { useState, useEffect } from "react";
import VoiceSearchButton from "./VoiceSearchButton";

export default function FilterBar({ listings, onFilter }) {
    const [search, setSearch] = useState("");
    const [province, setProvince] = useState("");
    const [category, setCategory] = useState("");
    const [rating, setRating] = useState("");
    const [sortBy, setSortBy] = useState("");
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [topRated, setTopRated] = useState(false);

    const provinceOptions = [...new Set((listings || []).map(b => b.province).filter(Boolean))];
    const categoryOptions = [...new Set((listings || []).map(b => b.catagory).filter(Boolean))];

    const [debouncedSearch, setDebouncedSearch] = useState(search);
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 150);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        let filtered = listings || [];

        if (debouncedSearch)
            filtered = filtered.filter(b => b.title?.toLowerCase().includes(debouncedSearch.toLowerCase()));
        if (province)
            filtered = filtered.filter(b => b.province === province);
        if (category)
            filtered = filtered.filter(b => b.catagory === category);
        if (verifiedOnly)
            filtered = filtered.filter(b => Number(b.is_verified) === 1);
        if (rating)
            filtered = filtered.filter(b => Math.floor(b.rating) >= parseInt(rating));

        if (topRated || sortBy === "top-rated")
            filtered = [...filtered].sort((a, b) => b.rating - a.rating);
        else if (sortBy === "newest")
            filtered = [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        if (typeof onFilter === "function") onFilter(filtered);
    }, [debouncedSearch, province, category, rating, sortBy, verifiedOnly, topRated, listings]);

    const resetFilters = () => {
        setSearch(""); setProvince(""); setCategory(""); setRating(""); setSortBy("");
        setVerifiedOnly(false); setTopRated(false);
        if (typeof onFilter === "function") onFilter(listings);
    };

    // keep the on-scroll shadow like before
    useEffect(() => {
        const el = document.getElementById("filter-bar");
        if (!el) return;
        const onScroll = () => {
            el.style.boxShadow = window.scrollY > 120 ? "0 6px 18px rgba(0,0,0,0.10)" : "none";
        };
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div id="filter-bar" className="jd-filter-wrap">
            <style>{`
               .jd-filter-wrap { position: sticky; top: 90px; z-index: 1000; background: #fff; }

                               .jd-filter-bar {
                    display: flex; align-items: center; gap: 7px;
                    padding: 8px 4px; overflow-x: auto; border-bottom: 1px solid #eef0f2;
                }
                .jd-filter-bar::-webkit-scrollbar { height: 0; }
                .jd-search {
                    display: flex; align-items: center; gap: 6px; background: #fff;
                    border: 1px solid #d6dbe1; border-radius: 20px; padding: 5px 12px;
                    min-width: 190px; flex: 0 0 auto;
                }
                .jd-search i { color: #8a9099; font-size: 13px; }
                .jd-search input { border: 0; outline: 0; font-size: 13px; width: 140px; background: transparent; }
                .jd-chip {
                    display: flex; align-items: center; gap: 5px; white-space: nowrap;
                    background: #fff; border: 1px solid #d6dbe1; border-radius: 20px;
                    padding: 5px 12px; font-size: 13px; color: #444; cursor: pointer; flex: 0 0 auto;
                    line-height: 1.2;
                }
                .jd-chip i { font-size: 13px; }
                .jd-chip:hover { border-color: #2563eb; }
                .jd-chip.active { background: #eef3ff; border-color: #2563eb; color: #2563eb; }
                .jd-chip i.bi-star-fill { color: #f5a623; }
                .jd-chip-select { position: relative; padding: 0; }
                .jd-chip-select select {
                    appearance: none; -webkit-appearance: none; border: 0; background: transparent;
                    padding: 5px 26px 5px 12px; font-size: 13px; color: #444; cursor: pointer; border-radius: 20px;
                }
                .jd-chip-select i { position: absolute; right: 10px; pointer-events: none; color: #8a9099; font-size: 11px; }
                .jd-reset { color: #dc3545; }

            `}</style>

            <div className="jd-filter-bar">
                {/* Search */}
                <div className="jd-search">
                    <i className="bi bi-search"></i>
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search by name..."
                    />
                    <VoiceSearchButton onResult={(text) => setSearch(text)} />
                </div>


                {/* Sort by */}
                <div className="jd-chip jd-chip-select">
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                        <option value="">Sort by</option>
                        <option value="newest">Newest</option>
                        <option value="top-rated">Top Rated</option>
                    </select>
                    <i className="bi bi-chevron-down"></i>
                </div>

                {/* Top Rated toggle */}
                <button className={`jd-chip ${topRated ? "active" : ""}`} onClick={() => setTopRated(v => !v)}>
                    <i className="bi bi-star-fill"></i> Top Rated
                </button>

                {/* Verified toggle */}
                <button className={`jd-chip ${verifiedOnly ? "active" : ""}`} onClick={() => setVerifiedOnly(v => !v)}>
                    <i className="bi bi-patch-check-fill"></i> Verified
                </button>

                {/* Province */}
                <div className="jd-chip jd-chip-select">
                    <select value={province} onChange={e => setProvince(e.target.value)}>
                        <option value="">All Provinces</option>
                        {provinceOptions.map((p, i) => <option key={i}>{p}</option>)}
                    </select>
                    <i className="bi bi-chevron-down"></i>
                </div>

                {/* Category */}
                <div className="jd-chip jd-chip-select">
                    <select value={category} onChange={e => setCategory(e.target.value)}>
                        <option value="">All Categories</option>
                        {categoryOptions.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                    <i className="bi bi-chevron-down"></i>
                </div>

                {/* Rating */}
                <div className="jd-chip jd-chip-select">
                    <select value={rating} onChange={e => setRating(e.target.value)}>
                        <option value="">Any Rating</option>
                        <option value="4">4★ &amp; up</option>
                        <option value="3">3★ & up</option>
                        <option value="2">2★ &amp; up</option>
                    </select>
                    <i className="bi bi-chevron-down"></i>
                </div>

                {/* Reset */}
                <button className="jd-chip jd-reset" onClick={resetFilters}>
                    <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
            </div>
        </div>
    );
}
